import React, { useRef, forwardRef, useImperativeHandle, useEffect, useState, useCallback } from "react";
import { Card, Typography, Box, useTheme, CircularProgress } from "@mui/material";
import ReactPlayer from "react-player";

const VideoPlayerCard = forwardRef(({
  interview,
  videoBlobUrl,
  questionText,
  transcriptText,
  autoPlayKey,
  onVideoPlay,
  loading = false,
  showNoVideo = false,
}, ref) => {
  const theme = useTheme();
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const pausePlayback = useCallback(() => {
    setIsPlaying(false);
    const video = playerRef.current;
    if (video && typeof video.pause === "function") {
      video.pause();
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      pause: pausePlayback,
      getVideoElement: () => playerRef.current,
    }),
    [pausePlayback],
  );

  const hasVideo =
    videoBlobUrl &&
    typeof videoBlobUrl === "string" &&
    videoBlobUrl.trim() !== "";

  const hasQuestion =
    questionText &&
    typeof questionText === "string" &&
    questionText.trim() !== "";

  const hasTranscript =
    transcriptText &&
    typeof transcriptText === "string" &&
    transcriptText.trim() !== "";


  useEffect(() => {
    setIsPlaying(false);
  }, [videoBlobUrl]);

  useEffect(() => {
    if (!hasVideo || !autoPlayKey) return;
    setIsPlaying(true);
  }, [autoPlayKey, hasVideo]);
  return (
    <Card
      elevation={0}
      sx={{
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      {hasVideo ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%",
            backgroundColor: "#000",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0, left: 0,
              width: "100%", height: "100%",
            }}
          >
            <ReactPlayer
              ref={playerRef}
              url={videoBlobUrl}
              src={videoBlobUrl}
              key={videoBlobUrl}
              playing={isPlaying}
              controls
              playsInline
              width="100%"
              height="100%"
              download={true}
              config={{
                file: {
                  attributes: {
                    crossOrigin: "anonymous",
                    preload: "auto",
                    controlsList: "download",
                  },
                },
              }}
              onPlay={() => {
                setIsPlaying(true);
                if (typeof onVideoPlay === "function") onVideoPlay();
              }}
              onPause={() => setIsPlaying(false)}
            />
          </Box>
        </Box>
      ) : loading ? (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "#f3f4f6",
          }}
        >
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading recording preview...
          </Typography>
        </Box>
      ) : showNoVideo ? (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "#f3f4f6",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No Recording Preview Available.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "#f3f4f6",
          }}
        />
      )}

      {hasQuestion && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.2)"
                : "rgba(0,0,0,0.02)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ display: "block", mb: 0.5 }}
          >
            Question
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.primary,
              minHeight: "2em",
              overflowWrap: "anywhere",
            }}
          >
            {questionText}
          </Typography>
        </Box>
      )}

      {hasTranscript && (
        <Box
          sx={{
            p: 2,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.3)"
                : "rgba(0,0,0,0.01)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ display: "block", mb: 0.5 }}
          >
            Transcript
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              maxHeight: 200,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {transcriptText}
          </Typography>
        </Box>
      )}
    </Card>
  );
});

VideoPlayerCard.displayName = "VideoPlayerCard";
export default VideoPlayerCard;